/** Channel detail (UX-SPEC §7.2): measurement, profile along the document, segments, units, evidence summary. */
import { useState } from 'preact/hooks';
import { CHANNEL_IDS } from '../../engine/channels/types.ts';
import type { ChannelSegment, UnitEstimate } from '../../engine/channels/types.ts';
import type { ChannelRow } from '../../engine/ensemble/index.ts';
import { FAMILY_KEY, type GlossaryKey } from '../../shared/help-types.ts';
import type { ChannelDetail, ViewModel } from '../../shared/viewmodel.ts';
import { ChannelAction } from '../bench/Actions.tsx';
import { DataTable, type SortState } from '../components/DataTable.tsx';
import { ExplainerLines, Term } from '../components/Term.tsx';
import { fmt1, fmt2, fmtInt, fmtInterval, fmtP, fmtSig, fmtSigned1 } from '../format.ts';
import { derive, firstSpanOf, isChannelId, stretchLabel } from '../model.ts';
import { hashFor } from '../router.ts';
import { DIRECTION, SCORE_NOTE } from '../strings-explain.ts';
import { CHANNELS as C, DETAIL as D, TRANSLATION as TR } from '../strings.ts';
import { Notice, Whisker } from '../ui.tsx';
import { ED14, isTranslationExplained } from './Channels.tsx';

const W = 1000;
const H = 240;
const PAD_B = 22;

function ProfileChart({ vm, ch }: { vm: ViewModel; ch: ChannelDetail }) {
	const n = Math.max(1, vm.paras.count);
	const x = (p: number) => (p / n) * W;
	const segs = ch.segments;
	const prof = ch.profile;
	let top = ch.priorMu;
	if (prof) for (const v of prof.y) if (v > top) top = v;
	for (const s of segs) top = Math.max(top, s.estimate, Math.min(s.ci90[1], (top || 1) * 3));
	const ymax = Math.max(1e-9, top) * 1.1;
	const y = (v: number) => (H - PAD_B) * (1 - Math.max(0, Math.min(ymax, v)) / ymax);
	const [cursor, setCursor] = useState<number | null>(null);
	const [live, setLive] = useState('');
	const flagged = ch.flagsWithheld ? [] : segs.filter((s) => s.hotspot || s.elevated);
	const describe = (p: number): string => {
		const seg = segs.find((s) => p >= s.paraStart && p < s.paraEnd);
		let rate = '';
		if (prof) {
			let best = 0;
			for (let i = 1; i < prof.x.length; i++)
				if (Math.abs((prof.x[i] as number) - p) < Math.abs((prof.x[best] as number) - p)) best = i;
			rate = `${fmtSig(prof.y[best] as number)} ${ch.unitLabel}`;
		}
		const at = stretchLabel(vm, Math.floor(p), Math.floor(p) + 1);
		return [
			at,
			rate && `rate ${rate}`,
			seg
				? `segment ${fmtSig(seg.estimate)} (${fmtSig(seg.ci90[0])}–${fmtSig(seg.ci90[1])}) ${ch.unitLabel}`
				: '',
		]
			.filter(Boolean)
			.join(' · ');
	};
	const posOf = (e: PointerEvent): number => {
		const r = (e.currentTarget as SVGElement).getBoundingClientRect();
		return Math.max(
			0,
			Math.min(n - 1, Math.floor(((e.clientX - r.left) / Math.max(1, r.width)) * n))
		);
	};
	const label = `${D.profileName(ch.id)}: ${segs.length} ${segs.length === 1 ? 'segment' : 'segments'}, reference ${fmtSig(ch.priorMu)} ${ch.unitLabel}`;
	return (
		<figure class="profile-fig">
			<svg
				class="profile"
				viewBox={`0 0 ${W} ${H}`}
				role="img"
				tabindex={0}
				aria-label={label}
				data-testid="profile-chart"
				data-points={prof?.x.length ?? 0}
				onPointerMove={(e) => {
					const p = posOf(e);
					setCursor(p);
					setLive(describe(p));
				}}
				onPointerLeave={() => setCursor(null)}
				onKeyDown={(e) => {
					const step = Math.max(1, Math.round(n / 100));
					const cur = cursor ?? 0;
					let next = cur;
					if (e.key === 'ArrowRight') next = Math.min(n - 1, cur + step);
					else if (e.key === 'ArrowLeft') next = Math.max(0, cur - step);
					else return;
					e.preventDefault();
					setCursor(next);
					setLive(describe(next));
				}}
				onBlur={() => setCursor(null)}
			>
				<line
					x1={0}
					x2={W}
					y1={y(0)}
					y2={y(0)}
					stroke="var(--stone-500)"
					stroke-width="1"
					vector-effect="non-scaling-stroke"
				/>
				{segs.map((s) => (
					<rect
						key={`b${s.paraStart}`}
						x={x(s.paraStart)}
						y={y(s.ci90[1])}
						width={Math.max(1, x(s.paraEnd) - x(s.paraStart))}
						height={Math.max(1, y(s.ci90[0]) - y(s.ci90[1]))}
						fill="var(--ev-100)"
					/>
				))}
				{prof ? (
					<polyline
						points={Array.from(prof.x, (px, i) => `${x(px)},${y(prof.y[i] as number)}`).join(' ')}
						fill="none"
						stroke="var(--ev-500)"
						stroke-width="1.5"
						vector-effect="non-scaling-stroke"
					/>
				) : null}
				{segs.map((s) => (
					<line
						key={`s${s.paraStart}`}
						x1={x(s.paraStart)}
						x2={x(s.paraEnd)}
						y1={y(s.estimate)}
						y2={y(s.estimate)}
						stroke="var(--ev-700)"
						stroke-width="2.5"
						vector-effect="non-scaling-stroke"
					/>
				))}
				<line
					x1={0}
					x2={W}
					y1={y(ch.priorMu)}
					y2={y(ch.priorMu)}
					stroke="var(--stone-500)"
					stroke-width="1.5"
					stroke-dasharray="6 4"
					vector-effect="non-scaling-stroke"
				/>
				<text class="axis-t ref-t" x={6} y={y(ch.priorMu) - 5}>
					{D.referenceLine}
				</text>
				<text
					class="axis-t"
					x={W - 6}
					y={ch.direction > 0 ? y(ch.priorMu) - 5 : y(ch.priorMu) + 13}
					text-anchor="end"
					data-testid="profile-direction"
				>
					{ch.direction > 0 ? DIRECTION.chartAbove : DIRECTION.chartBelow}
				</text>
				<text class="axis-t" x={6} y={12}>
					{fmtSig(ymax)} {ch.unitLabel}
				</text>
				<text class="axis-t" x={6} y={H - PAD_B - 4}>
					0
				</text>
				{vm.chapters.length > 1
					? vm.chapters.map((c) => (
							<line
								key={c.charStart}
								x1={x(c.paraStart)}
								x2={x(c.paraStart)}
								y1={H - PAD_B + 2}
								y2={H - PAD_B + 8}
								stroke="var(--stone-500)"
								stroke-width="1"
								vector-effect="non-scaling-stroke"
							/>
						))
					: null}
				{flagged.map((s) => (
					<line
						key={`f${s.paraStart}`}
						x1={x(s.paraStart)}
						x2={x(s.paraEnd)}
						y1={H - 5}
						y2={H - 5}
						stroke="var(--ink-900)"
						stroke-width={s.hotspot ? 4 : 2}
						vector-effect="non-scaling-stroke"
					>
						<title>{`${s.hotspot ? D.flagHotspot : D.flagElevated}: ${stretchLabel(vm, s.paraStart, s.paraEnd)}`}</title>
					</line>
				))}
				{cursor !== null ? (
					<line
						x1={x(cursor)}
						x2={x(cursor)}
						y1={0}
						y2={H - PAD_B}
						stroke="var(--moss-700)"
						stroke-width="1.5"
						vector-effect="non-scaling-stroke"
					/>
				) : null}
			</svg>
			<figcaption class="caption">{prof ? D.profileCaption : D.noProfile}</figcaption>
			<div class="visually-hidden" aria-live="polite">
				{live}
			</div>
			{cursor !== null ? (
				<p class="dim" data-testid="profile-tip">
					{describe(cursor)}
				</p>
			) : null}
		</figure>
	);
}

function flagOf(ch: ChannelDetail, s: ChannelSegment): string {
	if (ch.flagsWithheld) return D.flagWithheld;
	return s.hotspot ? D.flagHotspot : s.elevated ? D.flagElevated : D.flagNone;
}

function SegmentsTable({ vm, ch }: { vm: ViewModel; ch: ChannelDetail }) {
	return (
		<section aria-labelledby="h-segs">
			<h2 id="h-segs">{D.segmentsTitle}</h2>
			<p class="caption">
				{SCORE_NOTE.lead}
				<Term k="evidence-strength">{SCORE_NOTE.term}</Term>
				{SCORE_NOTE.segment}
			</p>
			<DataTable
				testId="segments-table"
				caption={D.segmentsTitle}
				rows={ch.segments}
				rowKey={(s) => s.paraStart}
				columns={[
					{
						id: 'range',
						header: D.cols.range,
						render: (s) => (
							<a
								href={hashFor('read', {
									params: { p: String(s.paraStart), ch: ch.id, hs: `${s.paraStart}-${s.paraEnd}` },
								})}
							>
								{stretchLabel(vm, s.paraStart, s.paraEnd)}
							</a>
						),
					},
					{
						id: 'est',
						header: `${D.cols.estimate} (${ch.unitLabel})`,
						num: true,
						render: (s) => `${fmtSig(s.estimate)} (${fmtSig(s.ci90[0])}–${fmtSig(s.ci90[1])})`,
					},
					{ id: 'score', header: D.cols.score, num: true, render: (s) => fmt1(s.score) },
					{
						id: 'flag',
						header: <Term k="hotspot">{D.cols.flag}</Term>,
						render: (s) => (
							<span class={ch.flagsWithheld ? 'note-notice' : undefined}>{flagOf(ch, s)}</span>
						),
					},
					{
						id: 'pref',
						header: <Term k="p-reference">{D.cols.pRef}</Term>,
						num: true,
						render: (s) => fmtP(s.pRef),
					},
					{
						id: 'pbg',
						header: <Term k="p-background">{D.cols.pBg}</Term>,
						num: true,
						render: (s) => fmtP(s.pBg),
					},
					{
						id: 'rr',
						header: <Term k="rate-ratio-reference">{D.cols.ratioRef}</Term>,
						num: true,
						render: (s) => (s.rateRatioRef === undefined ? '' : fmtSig(s.rateRatioRef)),
					},
					{
						id: 'rb',
						header: <Term k="rate-ratio-background">{D.cols.ratioBg}</Term>,
						num: true,
						render: (s) => (s.rateRatioBg === undefined ? '' : fmtSig(s.rateRatioBg)),
					},
				]}
			/>
		</section>
	);
}

function UnitsTable({ vm, ch }: { vm: ViewModel; ch: ChannelDetail }) {
	const [sort, setSort] = useState<SortState>({ id: 'unit', dir: 'asc' });
	const rows = ch.units.map((u, i) => ({ u, i }));
	const cmp: Record<
		string,
		(a: { u: UnitEstimate; i: number }, b: { u: UnitEstimate; i: number }) => number
	> = {
		unit: (a, b) => a.i - b.i,
		score: (a, b) => a.u.score - b.u.score,
		p: (a, b) => a.u.pRef - b.u.pRef,
	};
	const fn = cmp[sort?.id ?? 'unit'] ?? (cmp.unit as (typeof cmp)[string]);
	const sorted = [...rows].sort((a, b) => (sort?.dir === 'desc' ? fn(b, a) : fn(a, b)));
	const onSort = (id: string) =>
		setSort(
			sort?.id === id
				? { id, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
				: { id, dir: id === 'unit' ? 'asc' : 'desc' }
		);
	return (
		<section aria-labelledby="h-units">
			<h2 id="h-units">{D.unitsTitle}</h2>
			<p class="caption">
				{SCORE_NOTE.lead}
				<Term k="evidence-strength">{SCORE_NOTE.term}</Term>
				{SCORE_NOTE.chapter}
			</p>
			<DataTable
				testId="units-table"
				caption={D.unitsTitle}
				rows={sorted}
				rowKey={(r) => r.i}
				virtual={sorted.length > 200}
				sort={sort}
				onSort={onSort}
				columns={[
					{
						id: 'unit',
						header: D.cols.unit,
						sortable: true,
						render: (r) => (
							<a href={hashFor('read', { params: { p: String(r.u.paraStart), ch: ch.id } })}>
								{vm.chapters[r.i]?.title ??
									`${r.u.kind === 'scene' ? 'Scene' : 'Chapter'} ${r.i + 1}`}
							</a>
						),
					},
					{
						id: 'score',
						header: D.cols.score,
						sortable: true,
						num: true,
						render: (r) => fmt1(r.u.score),
					},
					{
						id: 'est',
						header: `${D.cols.estimate} (${ch.unitLabel})`,
						num: true,
						render: (r) =>
							`${fmtSig(r.u.estimate)} (${fmtSig(r.u.ci90[0])}–${fmtSig(r.u.ci90[1])})`,
					},
					{
						id: 'el',
						header: <Term k="elevated">{D.cols.elevated}</Term>,
						render: (r) => (ch.flagsWithheld ? D.flagWithheld : r.u.elevated ? D.yes : D.no),
					},
					{
						id: 'p',
						header: D.cols.pRef,
						sortable: true,
						num: true,
						render: (r) => fmtP(r.u.pRef),
					},
				]}
			/>
		</section>
	);
}

function Rail({ vm, current }: { vm: ViewModel; current: string }) {
	return (
		<nav class="rail" aria-label="Channels">
			<h2 class="rail-h">{C.title}</h2>
			<ul class="rail-list">
				{vm.ensemble.channels.map((r) => (
					<li key={r.id}>
						<a
							href={hashFor('channel', { id: r.id })}
							class={r.id === current ? 'is-current' : undefined}
							aria-current={r.id === current ? 'page' : undefined}
						>
							<span class="mono">{r.id}</span>
							<span class="rail-name">{r.name}</span>
							<span class="rail-score">{r.status === 'scored' ? fmt1(r.score ?? 0) : '—'}</span>
						</a>
					</li>
				))}
			</ul>
		</nav>
	);
}

/** What a translation-group channel measures, why it matters and how to read it (additive help; T2, CD-48). */
function TranslationExplainer({ id }: { id: 'MT-01' | 'TR-01' | 'TR-02' }) {
	const x = TR[id];
	return (
		<section aria-labelledby="h-explain" data-testid="translation-explainer">
			<h2 id="h-explain">{TR.explainTitle}</h2>
			<dl class="facts">
				<div class="wide">
					<dt>{TR.measuresLabel}</dt>
					<dd>{x.measures}</dd>
				</div>
				<div class="wide">
					<dt>{TR.whyLabel}</dt>
					<dd>{x.why}</dd>
				</div>
				<div class="wide">
					<dt>{TR.cautionLabel}</dt>
					<dd>{x.caution}</dd>
				</div>
				<div class="wide">
					<dt>{D.axis}</dt>
					<dd>{TR.scope}</dd>
				</div>
			</dl>
		</section>
	);
}

function Facts({ ch, row }: { ch: ChannelDetail; row: ChannelRow }) {
	const est = ch.estimate;
	const measured =
		est && (ch.estimator === 'rate' || ch.estimator === 'proportion')
			? D.measured(
					fmtSig(est.raw * ch.unitScale),
					ch.unitLabel,
					fmtInt(est.K),
					fmtInt(est.E),
					ch.exposureUnit
				)
			: null;
	return (
		<dl class="facts" data-testid="channel-facts">
			<div>
				<dt>{D.family}</dt>
				<dd>
					{FAMILY_KEY[ch.family] ? (
						<Term k={FAMILY_KEY[ch.family] as GlossaryKey}>{ch.family}</Term>
					) : (
						ch.family
					)}
				</dd>
			</div>
			<div>
				<dt>{DIRECTION.label}</dt>
				<dd>{ch.direction > 0 ? DIRECTION.higher : DIRECTION.lower}</dd>
			</div>
			<div>
				<dt>{D.axis}</dt>
				<dd>{ch.axis === 'translation' ? D.axisTranslation : D.axisAuthorship}</dd>
			</div>
			<div>
				<dt>
					<Term k="evidence-strength">{D.score}</Term>
				</dt>
				<dd class="big">
					{row.status === 'scored' ? (
						<>
							<span data-testid="channel-score">{fmt1(row.score ?? 0)}</span>
							{row.ci90 ? (
								<span class="dim sm">
									{' '}
									{fmtInterval(row.ci90)} ({row.intervalMethod ?? 'analytic'}){' '}
									<Whisker
										value={row.score ?? 0}
										lo={row.ci90[0]}
										hi={row.ci90[1]}
										width={110}
										hollow={(row.kappa ?? 1) < 0.2}
									/>
								</span>
							) : null}
						</>
					) : (
						C.abstained(row.reason ?? '')
					)}
				</dd>
			</div>
			{row.status === 'scored' ? (
				<>
					<div>
						<dt>
							<Term k="kappa">{D.kappa}</Term>
						</dt>
						<dd>
							{fmt2(row.kappa ?? 0)}
							{(row.kappa ?? 1) < 0.2 ? ` · ${C.littleEvidence}` : ''}
						</dd>
					</div>
					<div>
						<dt>
							<Term k="deviation">{D.deviation}</Term>
						</dt>
						<dd>
							{fmtSigned1(row.deviation ?? 0)}
							<span class="dim sm"> {DIRECTION.deviationHelp}</span>
						</dd>
					</div>
					{row.contribution !== undefined ? (
						<div>
							<dt>
								<Term k="contribution">{D.contribution}</Term>
							</dt>
							<dd>{fmt2(row.contribution)}</dd>
						</div>
					) : null}
				</>
			) : null}
			{measured ? (
				<div class="wide">
					<dt>Measured</dt>
					<dd>{measured}</dd>
				</div>
			) : null}
			{row.notes.length > 0 || row.referenceNote ? (
				<div class="wide">
					<dt>{C.cols.notes}</dt>
					<dd>
						{row.notes.map((n) => (
							<span key={n} class={n === ED14 ? 'note-notice' : 'note'}>
								{n}{' '}
							</span>
						))}
						{row.referenceNote ? (
							<span class="note" data-testid="reference-note">
								{row.referenceNote}
							</span>
						) : null}
					</dd>
				</div>
			) : null}
		</dl>
	);
}

export function ChannelDetailView({ vm, id }: { vm: ViewModel; id?: string }) {
	const d = derive(vm);
	if (!isChannelId(id) || !d.detail.get(id) || !d.row.get(id)) {
		return (
			<div class="wrap" data-testid="channel-missing">
				<h1>{C.title}</h1>
				<Notice kind="error">{D.notFound(id ?? '')}</Notice>
				<p>
					<a href={hashFor('channels')}>{D.back}</a>
				</p>
			</div>
		);
	}
	const ch = d.detail.get(id) as ChannelDetail;
	const row = d.row.get(id) as ChannelRow;
	const list = vm.ensemble.channels;
	const at = list.findIndex((r) => r.id === id);
	const prev = at > 0 ? list[at - 1] : undefined;
	const next = at < list.length - 1 ? list[at + 1] : undefined;
	const ci = CHANNEL_IDS.indexOf(id);
	const first = firstSpanOf(vm, ci);
	const firstPara = first >= 0 ? d.paraOf(vm.spans.start[first] as number) : 0;
	return (
		<div class="chdetail" data-testid="channel-detail" data-channel={id}>
			<Rail vm={vm} current={id} />
			<div class="main-col">
				<div class="ch-head">
					<div>
						<p class="eyebrow">
							<a href={hashFor('channels')}>{D.back}</a>
						</p>
						<h1>
							<span class="mono">{ch.id}</span> {ch.name}
						</h1>
						<div class="ch-explainer" data-testid="channel-explainer">
							<ExplainerLines id={id} />
						</div>
					</div>
					<nav class="prevnext" aria-label="Channel navigation">
						{prev ? (
							<a href={hashFor('channel', { id: prev.id })}>{`← ${D.previous} (${prev.id})`}</a>
						) : null}
						{next ? (
							<a href={hashFor('channel', { id: next.id })}>{`${D.next} (${next.id}) →`}</a>
						) : null}
					</nav>
					<ChannelAction id={id} />
				</div>
				{ch.flagsWithheld ? (
					<Notice kind="notice">{D.withheldBanner(ch.flagsWithheld)}</Notice>
				) : null}
				<Facts ch={ch} row={row} />
				{isTranslationExplained(id) ? <TranslationExplainer id={id} /> : null}
				<section aria-labelledby="h-profile">
					<h2 id="h-profile">{D.profileTitle}</h2>
					<ProfileChart vm={vm} ch={ch} />
				</section>
				<SegmentsTable vm={vm} ch={ch} />
				<UnitsTable vm={vm} ch={ch} />
				<section aria-labelledby="h-summary">
					<div class="sec-head">
						<h2 id="h-summary">{D.summaryTitle}</h2>
						{first >= 0 ? (
							<a
								class="btn btn-secondary"
								href={hashFor('read', {
									params: { ch: id, p: String(firstPara), sp: String(first) },
								})}
							>
								{D.showInReader}
							</a>
						) : null}
					</div>
					{ch.summary.length > 0 ? (
						<ul class="summary" data-testid="channel-summary">
							{ch.summary.map((l) => (
								<li key={l}>{l}</li>
							))}
						</ul>
					) : (
						<p class="dim">{D.noSpans}</p>
					)}
					{Object.keys(ch.namedFeatures).length > 0 ? (
						<>
							<h3>
								<Term k="feature">{D.featuresTitle}</Term>
							</h3>
							<dl class="kv">
								{Object.entries(ch.namedFeatures).map(([k, v]) => (
									<div key={k}>
										<dt class="mono">{k}</dt>
										<dd>{fmtSig(v)}</dd>
									</div>
								))}
							</dl>
						</>
					) : null}
					{ch.spanCount > 0 ? (
						<>
							<h3>
								<Term k="evidence-span">{D.spansTitle}</Term>
							</h3>
							<DataTable
								caption={D.spansTitle}
								rows={ch.features
									.map((f, i) => ({ f, n: ch.featureSpanCounts[i] ?? 0 }))
									.filter((r) => r.n > 0)}
								rowKey={(r) => r.f}
								columns={[
									{ id: 'f', header: D.cols.feature, render: (r) => r.f },
									{ id: 'n', header: D.cols.count, num: true, render: (r) => fmtInt(r.n) },
								]}
							/>
						</>
					) : null}
				</section>
			</div>
		</div>
	);
}
